import {
  type ReactEventHandler,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import { FIXED_IFRAME_MESSAGE_IDS } from "@/store/iframeSlice";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { useDevServerLaunched } from "@/utils/devServerUtils";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import type { NavTreeNode } from "@/utils/types";
import { useActiveProject, useAutoUpdateRef, useCanvasId } from "@/utils/utils";
import { useIframePoolUrl } from "../../Canvas/hooks/useStoryboardUrl";
import type { StoryControlValues } from "../../RightStyle/StoryControlsManager";
import { getFileKeyForComponent } from "./utils";
import { combineShellAndBody } from "@/components/Canvas/IframePool/utils";

interface AssetIframeProps {
  className?: string;
  component?: JsxElement;
  storyArgs?: StoryControlValues;
  onStoryArgsUpdated?: (args: StoryControlValues) => void;
  fallback?: React.ReactNode;
  onDomSnapshot?: (args: {
    outerHtml?: string;
    navTree?: NavTreeNode | null;
  }) => void;
  onImgGenerated?: (
    data: { light: Blob; dark: Blob; width: number; height: number } | null,
  ) => void;
  capture?: boolean;
}

export const AssetIframe = ({
  className,
  onStoryArgsUpdated,
  component,
  storyArgs,
  fallback,
  onDomSnapshot,
  onImgGenerated,
  capture,
}: AssetIframeProps) => {
  const canvasId = useCanvasId();

  const devServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  )[canvasId];

  const iframeId = useId().replace(/:/g, "-");
  const [refreshKey, setRefreshKey] = useState(0);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [size, setSize] = useState({ width: 0, height: 0 });

  const fileKeyRef = useRef<string>();
  const [resolvedFileKey, setResolvedFileKey] = useState<string>();

  const project = useActiveProject();
  const projectRef = useAutoUpdateRef(project);

  const [navigate, setNavigate] =
    useState<
      (args: {
        fileKeys: string[];
        storyArgs?: StoryControlValues | undefined;
        darkMode: boolean;
        storyboardId: string;
      }) => void
    >();
  const [port, setPort] = useState<MessagePort>();

  // Handle iframe load
  const handleLoad: ReactEventHandler<HTMLIFrameElement> = (e) => {
    if (!e.currentTarget.contentWindow) {
      console.error({
        msg: "[TEMPO DEBUG] AssetIframe iframe failed to load - no content window",
        assetFrameUrl: devServerUrl,
      });
      setRefreshKey((k) => k + 1);
      return;
    }
  };

  const useCanvasDarkMode = useDarkModeStore(
    (state) => state.canvasDarkModeStates[canvasId] || false,
  );

  const fileKey = component && getFileKeyForComponent(component, canvasId);

  useEffect(() => {
    if (!navigate || !fileKey || !component?.codebaseId) return;

    navigate({
      fileKeys: [fileKey],
      storyArgs,
      darkMode: useCanvasDarkMode,
      storyboardId: component.codebaseId,
    });
  }, [component?.codebaseId, fileKey, navigate, storyArgs, useCanvasDarkMode]);

  // Handle iframe error and retry logic
  const handleError = useCallback(() => {
    console.error({
      msg: "[TEMPO DEBUG] AssetIframe iframe failed to load - error",
      assetFrameUrl: devServerUrl,
    });
    setRefreshKey((k) => k + 1);
  }, [devServerUrl]);

  const devServerLaunched = useDevServerLaunched();

  useEffect(() => {
    if (devServerLaunched) {
      setRefreshKey((k) => k + 1);
    }
  }, [devServerLaunched]);

  useEffect(() => {
    if (
      capture &&
      port &&
      containerSize.width > 0 &&
      resolvedFileKey === fileKeyRef.current
    ) {
      port?.postMessage({
        id: "CAPTURE_IMAGE",
        type: "executeFunction",
        payload: {
          functionName: "captureComponentPreview",
          args: [{ width: containerSize.width, height: containerSize.height }],
        },
      });
    }
  }, [
    capture,
    port,
    containerSize.width,
    containerSize.height,
    resolvedFileKey,
  ]);

  // Build tempobook URL
  const tempobookUrl = useIframePoolUrl(iframeId);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(node);
  }, []);

  const scale = useMemo(
    () =>
      Math.min(
        containerSize.width / size.width,
        containerSize.height / size.height,
      ),
    [containerSize.width, containerSize.height, size.width, size.height],
  );

  const cleanupRef = useRef<() => void>(() => {});

  const isReady = !!navigate && size.width > 0 && size.height > 0;

  const betterRef = useCallback(
    (node: HTMLIFrameElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = () => {};
      }
      if (!node) return;
      const controller = new AbortController();
      const signal = controller.signal;
      cleanupRef.current = () => {
        controller.abort();
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === "UPDATED_STORY_ARGS") {
          if (
            event.source !== node.contentWindow ||
            event.data.payload?.fileKey !== fileKeyRef.current
          ) {
            return;
          }

          const { storyArgs } = event.data.payload;
          onStoryArgsUpdated?.(storyArgs);
        } else if (event.data.type === "TEMPOBOOK_READY") {
          if (event.source !== node.contentWindow) {
            return;
          }

          setNavigate(() => (args) => {
            fileKeyRef.current = args.fileKeys[0];
            const payload = {
              type: "TEMPOBOOK_NAVIGATE",
              payload: {
                ...args,
                enableAssetPreviewMode: true,
              },
            };

            node.contentWindow?.postMessage(payload, "*");
          });
        } else if (event.data.type === "SCRIPT_READY") {
          if (event.source !== node.contentWindow) {
            return;
          }

          const channel = new MessageChannel();
          const port = channel.port1;
          setPort(port);
          node.contentWindow?.postMessage("init", "*", [channel.port2]);
          port.onmessage = (event) => {
            if (
              event.data?.fileKey !== fileKeyRef.current &&
              event.data?.fileKeys?.[0] !== fileKeyRef.current
            ) {
              return;
            }

            if (
              event.data?.id === FIXED_IFRAME_MESSAGE_IDS.NAV_TREE ||
              event.data?.id === FIXED_IFRAME_MESSAGE_IDS.NEW_OUTER_HTML_ONLY
            ) {
              const shellHtml = event.data?.shellHtml;
              const bodyHtml = event.data?.bodyHtml;
              const combinedHtml = combineShellAndBody(shellHtml, bodyHtml);
              // Relay DOM snapshot information upstream so parent can use it
              onDomSnapshot?.({
                outerHtml: combinedHtml,
                navTree: event.data?.navTree ?? null,
              });
            } else if (
              event.data?.id ===
              FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_CAPTURED
            ) {
              const { imageData, darkImageData, width, height } = event.data;
              onImgGenerated?.(
                imageData
                  ? {
                      light: imageData,
                      dark: darkImageData ?? imageData,
                      width,
                      height,
                    }
                  : null,
              );
            } else if (
              event.data?.id ===
              FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_ERROR
            ) {
              onImgGenerated?.(null);
            }
          };

          port.postMessage({
            id: "REGISTER_EVENT_LISTENERS",
            type: "executeFunction",
            payload: {
              functionName: "initProject",
              args: [null, projectRef?.current?.framework],
            },
          });
        } else if (event.data.type === "ERROR") {
          if (event.source !== node.contentWindow) {
            return;
          }

          if (event.data.error) {
            handleError();
          }
        } else if (event.data.type === "SIZE_CHANGED") {
          if (
            event.source !== node.contentWindow ||
            event.data.payload?.fileKey !== fileKeyRef.current
          ) {
            return;
          }

          const { width, height } = event.data.payload;
          setSize({ width, height });
          setResolvedFileKey(event.data.payload?.fileKey);
        }
      };

      window.addEventListener("message", handleMessage, { signal });
    },
    [
      onStoryArgsUpdated,
      projectRef,
      onDomSnapshot,
      onImgGenerated,
      handleError,
    ],
  );

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden",
        className,
      )}
      ref={containerRef}
    >
      {!!devServerUrl && (
        // biome-ignore lint/a11y/useIframeTitle: is not visible to the user
        <iframe
          ref={betterRef}
          id={iframeId}
          key={refreshKey}
          src={tempobookUrl}
          className={cn(
            "[border:0] bg-transparent shrink-0 pointer-events-none",
            !isReady && "opacity-0 fixed w-0 h-0",
          )}
          style={
            size.width > 0 && size.height > 0
              ? {
                  width: size.width,
                  height: size.height,
                  transform: `scale(${scale})`,
                  transformOrigin: "center",
                }
              : undefined
          }
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {(!devServerUrl || !isReady) && fallback}
    </div>
  );
};

AssetIframe.displayName = "AssetIframe";
